import { Injectable, OnDestroy, Injector } from '@angular/core';
import { LogRecord, LoggingMetrics } from '../models';
import { environment } from 'src/environments/environment';
import { 
  PRODUCTION_LOGGING_CONFIG, 
  DEVELOPMENT_LOGGING_CONFIG, 
  STAGING_LOGGING_CONFIG,
  LoggingConfig 
} from '../models/logging-config.interface';
import { METADATA, CLOUDWATCH_ENDPOINTS, LOG_GROUP_NAMES } from '../constants';

/**
 * Configuration interface for LogQueueManager
 */
export interface LogQueueConfig {
  /** Maximum number of logs in batch */
  batchSize: number;
  /** Flush interval in milliseconds */
  flushIntervalMs: number;
  /** Maximum retry attempts */
  maxRetries: number;
  /** Maximum queue size before dropping logs */
  maxQueueSize: number;
}

/**
 * Error types for retry logic classification
 */
export enum ErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTHENTICATION_ERROR = 'AUTH_ERROR',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  RATE_LIMIT = 'RATE_LIMIT',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * Retry attempt information
 */
interface RetryAttempt {
  batch: LogRecord[];
  attempt: number;
  lastError?: any;
}

/**
 * Default configuration for LogQueueManager
 */
const DEFAULT_CONFIG: LogQueueConfig = {
  batchSize: 25,
  flushIntervalMs: 5000,
  maxRetries: 3,
  maxQueueSize: 1000
};

/**
 * LogQueueManager handles efficient log transmission with batching,
 * retry logic, and performance optimization
 */
@Injectable({
  providedIn: 'root'
})
export class LogQueueManager implements OnDestroy {
  private logQueue: LogRecord[] = [];
  private config: LogQueueConfig;
  private flushTimer: any;
  private metrics: LoggingMetrics = {
    queueSize: 0,
    failedTransmissions: 0,
    droppedLogs: 0,
    totalProcessed: 0,
    cloudWatchEnabled: false
  };
  private batchProcessingTimes: number[] = [];
  private isDestroyed = false;
  private retryQueue: RetryAttempt[] = [];
  private circuitBreakerFailures = 0;
  private circuitBreakerThreshold = 5;
  private circuitBreakerResetTime = 60000; // 1 minute
  private lastCircuitBreakerReset = 0;
  private metricsReportingTimer: any;
  private isDevelopmentMode = false;
  private writeLoggingService: any = null;
  private injector: Injector;
  private enableConsoleOutput = true; // Track console output setting

  // Failed logs retry mechanism
  private readonly FAILED_LOGS_KEY = 'ccmc_failed_logs';
  private readonly MAX_FAILED_LOGS_STORAGE = 50; // Limit to 50 logs in localStorage
  private readonly FAILED_LOGS_MAX_AGE = 24 * 60 * 60 * 1000; // 24 hours

  constructor(injector: Injector) {
    this.injector = injector;
    this.isDevelopmentMode = this.detectDevelopmentMode();
    this.config = this.getEnvironmentConfig();
    
    // Respect environment-specific console output settings
    // In test and production, console output should be disabled
    const configuredConsoleOutput = environment.logging?.enableConsoleOutput ?? true;
    this.enableConsoleOutput = configuredConsoleOutput && !environment.test && !environment.production;
    
    this.startFlushTimer();
    
    // Only start metrics reporting in development (never in test or production)
    if (this.isDevelopmentMode && !environment.production && !environment.test) {
      this.startMetricsReporting();
    }
    
    // Lazy initialize WriteLoggingService to avoid circular dependencies
    this.initializeWriteLoggingService();
    
    // Check for failed logs from previous sessions and retry
    this.retryFailedLogsFromStorage();
  }

  /**
   * Initialize WriteLoggingService with lazy injection
   */
  private initializeWriteLoggingService(): void {
    setTimeout(() => {
      try {
        // Import WriteLoggingService class for proper injection
        import('./write-logging.service').then(module => {
          try {
            this.writeLoggingService = this.injector.get(module.WriteLoggingService);
            this.logDebug('WriteLoggingService initialized:', !!this.writeLoggingService);
            this.logDebug('writeStructuredLogBatch available:', !!(this.writeLoggingService && typeof this.writeLoggingService.writeStructuredLogBatch === 'function'));
          } catch (injectionError) {
            this.logError('WriteLoggingService injection failed:', injectionError);
          }
        }).catch((importError) => {
          this.logError('WriteLoggingService module import failed:', importError);
        });
      } catch (error) {
        this.logError('WriteLoggingService initialization error:', error);
      }
    }, 0);
  }

  /**
   * Helper method for conditional debug logging
   */
  private logDebug(...args: any[]): void {
    if (this.enableConsoleOutput) {
      console.log(...args);
    }
  }

  /**
   * Helper method for conditional warning logging
   */
  private logWarn(...args: any[]): void {
    if (this.enableConsoleOutput) {
      console.warn(...args);
    }
  }

  /**
   * Helper method for conditional error logging
   */
  private logError(...args: any[]): void {
    if (this.enableConsoleOutput) {
      console.error(...args);
    }
  }

  /**
   * Add a log record to the queue for batched transmission
   * @param logRecord The log record to enqueue
   */
  enqueue(logRecord: LogRecord): void {

    if (this.isDestroyed) {
      return;
    }

    // Handle queue overflow by dropping oldest logs
    if (this.logQueue.length >= this.config.maxQueueSize) {
      this.logQueue.shift(); // Remove oldest log
      this.metrics.droppedLogs++;
    }

    this.logQueue.push(logRecord);
    this.metrics.queueSize = this.logQueue.length;

    // Check if batch size reached for immediate flush
    if (this.logQueue.length >= this.config.batchSize) {
      this.flushBatch();
    }
  }

  /**
   * Send ERROR level logs immediately, bypassing the queue
   * @param logRecord The ERROR level log record to send immediately
   */
  sendImmediate(logRecord: LogRecord): void {
    if (this.isDestroyed) {
      return;
    }

    // Send immediately without queuing
    this.sendBatch([logRecord]);
  }

  /**
   * Get current logging metrics
   * @returns Current LoggingMetrics
   */
  getMetrics(): LoggingMetrics {
    return {
      ...this.metrics,
      queueSize: this.logQueue.length,
      avgBatchProcessingTime: this.calculateAverageProcessingTime()
    };
  }

  /**
   * Get detailed metrics for monitoring and debugging
   * @returns Extended metrics object
   */
  getDetailedMetrics(): LoggingMetrics & {
    circuitBreakerFailures: number;
    circuitBreakerOpen: boolean;
    retryQueueSize: number;
    batchProcessingTimes: number[];
  } {
    return {
      ...this.getMetrics(),
      circuitBreakerFailures: this.circuitBreakerFailures,
      circuitBreakerOpen: this.isCircuitBreakerOpen(),
      retryQueueSize: this.retryQueue.length,
      batchProcessingTimes: [...this.batchProcessingTimes]
    };
  }

  /**
   * Flush all remaining logs in the queue
   * Used during application shutdown
   */
  flush(): Promise<void> {
    return new Promise((resolve) => {
      if (this.logQueue.length === 0) {
        resolve();
        return;
      }

      // Set a timeout to ensure flush doesn't hang indefinitely
      const flushTimeout = setTimeout(() => {
        this.logWarn('LogQueueManager: Flush timeout reached, forcing completion');
        resolve();
      }, 5000); // 5 second timeout

      // Flush all remaining batches
      const flushPromises: Promise<void>[] = [];
      
      while (this.logQueue.length > 0) {
        const batch = this.logQueue.splice(0, this.config.batchSize);
        this.metrics.queueSize = this.logQueue.length;
        
        // Send batch synchronously during flush
        const flushPromise = this.sendBatchSynchronously(batch);
        flushPromises.push(flushPromise);
      }

      Promise.allSettled(flushPromises).finally(() => {
        clearTimeout(flushTimeout);
        resolve();
      });
    });
  }

/**
 * Flush logs during page unload using the sendBeacon API.
 * Fire-and-forget and non-blocking; safe to call from beforeunload handlers.
 */
  flushWithBeacon(): void {
    if (this.logQueue.length === 0) {
      return;
    }

    // Use sendBeacon API if available
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      // Get all queued logs but DON'T clear queue yet
      const allLogs = [...this.logQueue];
      
      try {
        // Get CloudWatch endpoint
        const endpoint = this.getBeaconEndpoint();
        
        if (endpoint) {
          // Send logs in batches using the same format as WriteLoggingService
          this.sendLogsViaBeacon(allLogs, endpoint);
          // Only clear queue after successful send attempt
          this.logQueue = [];
          this.metrics.queueSize = 0;
        } else {
          // No endpoint configured, fall back to console logging
          this.fallbackToConsoleLogging(allLogs);
          // Clear queue after fallback
          this.logQueue = [];
          this.metrics.queueSize = 0;
        }
      } catch (error) {
        this.logError('LogQueueManager: sendBeacon failed:', error);
        // Use the original logs (allLogs) for fallback, not the empty queue
        this.fallbackToConsoleLogging(allLogs);
        // Clear queue after fallback
        this.logQueue = [];
        this.metrics.queueSize = 0;
      }
    } else {
      // sendBeacon not available, fall back to console logging
      const allLogs = [...this.logQueue];
      this.fallbackToConsoleLogging(allLogs);
      this.logQueue = [];
      this.metrics.queueSize = 0;
    }
  }

  /**
   * Send logs via sendBeacon using CloudWatch API format
   */
  private sendLogsViaBeacon(logs: LogRecord[], endpoint: string): void {
    // Group logs by session ID (same as WriteLoggingService)
    const logsBySession = this.groupLogsBySession(logs);
    
    let successCount = 0;
    let failureCount = 0;
    const failedLogs: LogRecord[] = [];

    // Send each session's logs as a separate beacon request
    Object.entries(logsBySession).forEach(([sessionId, sessionLogs]) => {
      sessionLogs.forEach(logRecord => {
        try {
          // Use the same format as WriteLoggingService
          const payload = JSON.stringify({
            logStream: sessionId,
            logGroupName: this.getLogGroupName(),
            log: logRecord
          });

          // Send via beacon
          const success = navigator.sendBeacon(endpoint, payload);
          
          if (success) {
            successCount++;
          } else {
            failureCount++;
            failedLogs.push(logRecord);
          }
        } catch (error) {
          failureCount++;
          failedLogs.push(logRecord);
          this.logError('LogQueueManager: Error sending individual log via beacon:', error);
        }
      });
    });

    // Update metrics
    this.metrics.totalProcessed += successCount;
    if (successCount > 0) {
      this.metrics.lastSuccessfulTransmission = new Date().toISOString();
    }
    if (failureCount > 0) {
      this.metrics.droppedLogs += failureCount;
      this.metrics.failedTransmissions++;
      
      // Fall back to console logging for actually failed logs
      this.fallbackToConsoleLogging(failedLogs);
    }
  }

  /**
   * Group logs by session ID for batch transmission
   */
  private groupLogsBySession(logs: LogRecord[]): Record<string, LogRecord[]> {
    return logs.reduce((acc, log) => {
      const sessionId = log.kinective.sessionId;
      if (!acc[sessionId]) {
        acc[sessionId] = [];
      }
      acc[sessionId].push(log);
      return acc;
    }, {} as Record<string, LogRecord[]>);
  }

  /**
   * Get the appropriate log group name based on environment
   */
  private getLogGroupName(): string {
    if (environment.production) {
      return LOG_GROUP_NAMES.PRODUCTION;
    } else if (environment.test) {
      return LOG_GROUP_NAMES.TEST;
    } else {
      return LOG_GROUP_NAMES.DEVELOPMENT;
    }
  }

  /**
   * Get the endpoint URL for sendBeacon transmission
   * Uses the same CloudWatch endpoint as WriteLoggingService
   */
  private getBeaconEndpoint(): string | null {
    try {
      const Buffer = require('buffer').Buffer;
      const baseUrl = Buffer.from(environment.environmentURL, 'base64').toString();
      
      if (!baseUrl) {
        return null;
      }
      
      return `${baseUrl}${CLOUDWATCH_ENDPOINTS.SINGLE_LOG}`;
    } catch (error) {
      this.logError('LogQueueManager: Error getting beacon endpoint:', error);
      return null;
    }
  }

  /**
   * Fallback to console logging when sendBeacon fails or isn't available
   */
  private fallbackToConsoleLogging(logs: LogRecord[]): void {
    if (this.enableConsoleOutput) {
      logs.forEach(logRecord => {
        const level = logRecord.level.toLowerCase();
        const consoleMethod = (console as any)[level] || console.log;
        consoleMethod(`[UNLOAD] [${logRecord.timestamp}] ${logRecord.level}: ${logRecord.message}`, logRecord.data);
      });
    }
    this.metrics.droppedLogs += logs.length;
    
    // Store failed logs for retry on next session
    this.storeFailedLogsForRetry(logs);
  }

  /**
   * Store failed logs in localStorage for retry on next session
   */
  private storeFailedLogsForRetry(failedLogs: LogRecord[]): void {
    try {
      if (typeof localStorage === 'undefined') {
        return; // No localStorage available
      }

      // Get existing failed logs
      const existingData = this.getStoredFailedLogs();
      const existingLogs = existingData?.logs || [];
      
      // Combine with new failed logs, but limit total count
      const allFailedLogs = [...existingLogs, ...failedLogs];
      const limitedLogs = allFailedLogs.slice(-this.MAX_FAILED_LOGS_STORAGE); // Keep only the most recent logs
      
      // Store with metadata
      const storageData = {
        logs: limitedLogs,
        timestamp: Date.now(),
        retryAttempted: existingData?.retryAttempted || false
      };
      
      localStorage.setItem(this.FAILED_LOGS_KEY, JSON.stringify(storageData));
      
      if (limitedLogs.length >= this.MAX_FAILED_LOGS_STORAGE) {
        this.logWarn(`LogQueueManager: Failed logs storage limit reached (${this.MAX_FAILED_LOGS_STORAGE}). Oldest logs discarded.`);
      }
      
    } catch (error) {
      this.logError('LogQueueManager: Error storing failed logs:', error);
    }
  }

  /**
   * Get stored failed logs from localStorage
   */
  private getStoredFailedLogs(): { logs: LogRecord[]; timestamp: number; retryAttempted: boolean } | null {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }

      const stored = localStorage.getItem(this.FAILED_LOGS_KEY);
      if (!stored) {
        return null;
      }

      const data = JSON.parse(stored);
      
      // Check if data is too old
      const age = Date.now() - data.timestamp;
      if (age > this.FAILED_LOGS_MAX_AGE) {
        localStorage.removeItem(this.FAILED_LOGS_KEY);
        return null;
      }

      return data;
    } catch (error) {
      this.logError('LogQueueManager: Error reading stored failed logs:', error);
      // Clear corrupted data
      try {
        localStorage.removeItem(this.FAILED_LOGS_KEY);
      } catch (e) {
        // Silent failure
      }
      return null;
    }
  }

  /**
   * Retry failed logs from previous sessions
   */
  private retryFailedLogsFromStorage(): void {
    // Delay retry to ensure services are initialized and user might be authenticated
    setTimeout(() => {
      try {
        const storedData = this.getStoredFailedLogs();
        
        if (!storedData || storedData.retryAttempted || storedData.logs.length === 0) {
          return; // No logs to retry or already attempted
        }

        // Mark as retry attempted to prevent multiple attempts
        this.markRetryAttempted();

        // Check if we have WriteLoggingService available for authenticated retry
        if (!this.writeLoggingService) {
          this.logDebug('LogQueueManager: WriteLoggingService not available for failed logs retry');
          return;
        }

        this.logDebug(`LogQueueManager: Retrying ${storedData.logs.length} failed logs from previous session`);
        
        // Attempt to send failed logs with proper authentication
        this.retryFailedLogsWithAuth(storedData.logs);
        
      } catch (error) {
        this.logError('LogQueueManager: Error during failed logs retry:', error);
      }
    }, 5000); // Wait 5 seconds for services to initialize
  }

  /**
   * Retry failed logs with proper authentication
   */
  private async retryFailedLogsWithAuth(logs: LogRecord[]): Promise<void> {
    try {
      // Use the regular sendBatchWithRetry method which includes authentication
      await this.sendBatchWithRetry(logs, 0);
      
      // Success - clear stored failed logs
      this.clearStoredFailedLogs();
      this.logDebug(`LogQueueManager: Successfully retried ${logs.length} failed logs`);
      
    } catch (error) {
      this.logWarn(`LogQueueManager: Failed to retry ${logs.length} logs, keeping in localStorage:`, error);
      // Keep the logs in storage, they'll be cleaned up after 24 hours
    }
  }

  /**
   * Mark retry as attempted in localStorage
   */
  private markRetryAttempted(): void {
    try {
      const storedData = this.getStoredFailedLogs();
      if (storedData) {
        storedData.retryAttempted = true;
        localStorage.setItem(this.FAILED_LOGS_KEY, JSON.stringify(storedData));
      }
    } catch (error) {
      this.logError('LogQueueManager: Error marking retry attempted:', error);
    }
  }

  /**
   * Clear stored failed logs from localStorage
   */
  private clearStoredFailedLogs(): void {
    try {
      localStorage.removeItem(this.FAILED_LOGS_KEY);
    } catch (error) {
      this.logError('LogQueueManager: Error clearing stored failed logs:', error);
    }
  }

  /**
   * Update configuration
   * @param config Partial configuration to update
   */
  updateConfig(config: Partial<LogQueueConfig>): void {
    this.config = { ...this.config, ...config };
    
    // Restart timer with new interval if changed
    if (config.flushIntervalMs) {
      this.stopFlushTimer();
      this.startFlushTimer();
    }
  }

  /**
   * Enable CloudWatch logging
   */
  enableCloudWatch(): void {
    this.metrics.cloudWatchEnabled = true;
  }

  /**
   * Disable CloudWatch logging
   */
  disableCloudWatch(): void {
    this.metrics.cloudWatchEnabled = false;
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.stopFlushTimer();
    this.stopMetricsReporting();
    
    // Flush remaining logs before destruction
    this.flush().then(() => {
      this.logDebug('LogQueueManager: Cleanup completed');
    }).catch((error) => {
      this.logError('LogQueueManager: Error during cleanup:', error);
    });
  }

  /**
   * Check if WriteLoggingService is available
   * @returns True if service is available
   */
  private isWriteLoggingServiceAvailable(): boolean {
    return !!(this.writeLoggingService && 
              (typeof this.writeLoggingService.writeLogsBatch === 'function' ||
               typeof this.writeLoggingService.writeStructuredLogBatch === 'function') &&
              !this.isCircuitBreakerOpen());
  }

  /**
   * Handle graceful degradation when WriteLoggingService is unavailable
   * @param batch Array of log records that couldn't be sent
   */
  private handleServiceUnavailable(batch: LogRecord[]): void {
    // Log to console as fallback (only if console output is enabled)
    this.logWarn('LogQueueManager: WriteLoggingService unavailable, falling back to console logging');
    
    if (this.enableConsoleOutput) {
      batch.forEach(logRecord => {
        const level = logRecord.level.toLowerCase();
        const consoleMethod = (console as any)[level] || console.log;
        consoleMethod(`[${logRecord.timestamp}] ${logRecord.level}: ${logRecord.message}`, logRecord.data);
      });
    }
    
    // Update metrics
    this.metrics.failedTransmissions++;
    this.metrics.droppedLogs += batch.length;
  }

  /**
   * Start the flush timer for periodic batch processing
   */
  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      if (this.logQueue.length > 0) {
        this.flushBatch();
      }
    }, this.config.flushIntervalMs);
  }

  /**
   * Stop the flush timer
   */
  private stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Flush current batch of logs
   */
  private flushBatch(): void {
    if (this.logQueue.length === 0) {
      return;
    }

    const batch = this.logQueue.splice(0, this.config.batchSize);
    this.metrics.queueSize = this.logQueue.length;
    this.sendBatch(batch);
  }

  /**
   * Send a batch of logs with retry mechanism
   * @param batch Array of log records to send
   */
  private sendBatch(batch: LogRecord[]): void {
    // Check circuit breaker
    if (this.isCircuitBreakerOpen()) {
      this.metrics.droppedLogs += batch.length;
      return;
    }

    this.sendBatchWithRetry(batch, 0);
  }

  /**
   * Send batch with retry logic and exponential backoff
   * @param batch Array of log records to send
   * @param attempt Current attempt number (0-based)
   */
  private async sendBatchWithRetry(batch: LogRecord[], attempt: number): Promise<void> {
    const startTime = Date.now();
    
    try {
      // Use WriteLoggingService for actual transmission
      if (this.writeLoggingService && typeof this.writeLoggingService.writeStructuredLogBatch === 'function') {

        // Convert batch to format expected by service
        const logsWithSessionId = batch.map(record => ({
          record: record,
          sessionId: record.kinective.sessionId
        }));
        
        // Service returns Observable, convert to Promise
        await this.writeLoggingService.writeStructuredLogBatch(logsWithSessionId).toPromise();
      } else {
        await this.simulateTransmission(batch);
      }
      
      // Success - reset circuit breaker failures
      this.circuitBreakerFailures = 0;
      this.metrics.totalProcessed += batch.length;
      
      const processingTime = Date.now() - startTime;
      this.batchProcessingTimes.push(processingTime);
      
      // Keep only last 100 processing times for average calculation
      if (this.batchProcessingTimes.length > 100) {
        this.batchProcessingTimes.shift();
      }
      
      this.metrics.lastSuccessfulTransmission = new Date().toISOString();
      
    } catch (error) {
      const errorType = this.classifyError(error);
      
      // Handle specific error types
      if (errorType === ErrorType.PAYLOAD_TOO_LARGE) {
        // Drop logs immediately for payload too large
        this.metrics.droppedLogs += batch.length;
        this.metrics.failedTransmissions++;
        return;
      }
      
      if (errorType === ErrorType.AUTHENTICATION_ERROR) {
        // Disable CloudWatch for auth errors
        this.disableCloudWatch();
        this.metrics.droppedLogs += batch.length;
        this.metrics.failedTransmissions++;
        return;
      }
      
      // Retry logic for retryable errors
      if (attempt < this.config.maxRetries) {
        const delay = this.calculateBackoffDelay(attempt);
        setTimeout(() => {
          this.sendBatchWithRetry(batch, attempt + 1);
        }, delay);
      } else {
        // Max retries exceeded
        this.metrics.failedTransmissions++;
        this.metrics.droppedLogs += batch.length;
        this.circuitBreakerFailures++;
        
        // Check if circuit breaker should open
        if (this.circuitBreakerFailures >= this.circuitBreakerThreshold) {
          this.lastCircuitBreakerReset = Date.now();
        }
      }
    }
  }

  /**
   * Send batch synchronously during flush operations
   * @param batch Array of log records to send
   */
  private async sendBatchSynchronously(batch: LogRecord[]): Promise<void> {
    if (!this.isWriteLoggingServiceAvailable()) {
      this.handleServiceUnavailable(batch);
      return;
    }

    try {
      if (this.writeLoggingService) {
        let result;
        
        // Try new WriteLoggingService method first
        if (typeof this.writeLoggingService.writeLogsBatch === 'function') {
          result = await this.writeLoggingService.writeLogsBatch(batch);
        }
        // Fall back to old WriteLoggingService method
        else if (typeof this.writeLoggingService.writeStructuredLogBatch === 'function') {
          // Convert batch to format expected by old service
          const logsWithSessionId = batch.map(record => ({
            record: record,
            sessionId: record.kinective.sessionId
          }));
          
          result = await this.writeLoggingService.writeStructuredLogBatch(logsWithSessionId).toPromise();
          result = { success: true };
        }
        
        if (result && !result.success) {
          throw new Error(result.message || 'WriteLoggingService failed');
        }
      } else {
        await this.simulateTransmission(batch);
      }
      this.metrics.totalProcessed += batch.length;
      this.metrics.lastSuccessfulTransmission = new Date().toISOString();
    } catch (error) {
      this.logError('LogQueueManager: Error during synchronous batch send:', error);
      this.handleServiceUnavailable(batch);
    }
  }

  /**
   * Simulate transmission for testing (placeholder for WriteLoggingService)
   * @param batch Array of log records
   */
  private async simulateTransmission(batch: LogRecord[]): Promise<void> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 10));
    
    // Simulate occasional failures for testing
    if (Math.random() < 0.05) { // 5% failure rate
      throw new Error('Simulated network error');
    }
  }

  /**
   * Classify error type for appropriate handling
   * @param error The error to classify
   * @returns ErrorType classification
   */
  private classifyError(error: any): ErrorType {
    if (!error) {
      return ErrorType.UNKNOWN_ERROR;
    }
    
    const errorMessage = error.message?.toLowerCase() || '';
    const statusCode = error.status || error.statusCode;
    
    if (statusCode === 413 || errorMessage.includes('payload too large')) {
      return ErrorType.PAYLOAD_TOO_LARGE;
    }
    
    if (statusCode === 401 || statusCode === 403 || errorMessage.includes('unauthorized') || errorMessage.includes('forbidden')) {
      return ErrorType.AUTHENTICATION_ERROR;
    }
    
    if (statusCode === 429 || errorMessage.includes('rate limit')) {
      return ErrorType.RATE_LIMIT;
    }
    
    if (statusCode >= 500 || errorMessage.includes('server error')) {
      return ErrorType.SERVER_ERROR;
    }
    
    if (errorMessage.includes('network') || errorMessage.includes('connection') || errorMessage.includes('timeout')) {
      return ErrorType.NETWORK_ERROR;
    }
    
    return ErrorType.UNKNOWN_ERROR;
  }

  /**
   * Calculate exponential backoff delay
   * @param attempt Current attempt number (0-based)
   * @returns Delay in milliseconds
   */
  private calculateBackoffDelay(attempt: number): number {
    // Exponential backoff: 1s, 2s, 4s
    const baseDelay = 1000; // 1 second
    return baseDelay * Math.pow(2, attempt);
  }

  /**
   * Check if circuit breaker is open
   * @returns True if circuit breaker is open
   */
  private isCircuitBreakerOpen(): boolean {
    if (this.circuitBreakerFailures < this.circuitBreakerThreshold) {
      return false;
    }
    
    // Check if reset time has passed
    const now = Date.now();
    if (now - this.lastCircuitBreakerReset > this.circuitBreakerResetTime) {
      this.circuitBreakerFailures = 0;
      return false;
    }
    
    return true;
  }

  /**
   * Calculate average batch processing time
   * @returns Average processing time in milliseconds
   */
  private calculateAverageProcessingTime(): number {
    if (this.batchProcessingTimes.length === 0) {
      return 0;
    }
    
    const sum = this.batchProcessingTimes.reduce((acc, time) => acc + time, 0);
    return Math.round(sum / this.batchProcessingTimes.length);
  }

  /**
   * Start metrics reporting for development mode
   */
  private startMetricsReporting(): void {
    this.metricsReportingTimer = setInterval(() => {
      this.reportMetrics();
    }, 30000); // Report every 30 seconds
  }

  /**
   * Stop metrics reporting
   */
  private stopMetricsReporting(): void {
    if (this.metricsReportingTimer) {
      clearInterval(this.metricsReportingTimer);
      this.metricsReportingTimer = null;
    }
  }

  /**
   * Report metrics to console (development mode only)
   */
  private reportMetrics(): void {
    // Double-check: never show metrics in production or test
    if (!this.isDevelopmentMode || environment.production || environment.test) {
      return;
    }

    const metrics = this.getDetailedMetrics();
    const report = {
      timestamp: new Date().toISOString(),
      queueSize: metrics.queueSize,
      totalProcessed: metrics.totalProcessed,
      failedTransmissions: metrics.failedTransmissions,
      droppedLogs: metrics.droppedLogs,
      avgBatchProcessingTime: metrics.avgBatchProcessingTime,
      cloudWatchEnabled: metrics.cloudWatchEnabled,
      circuitBreakerOpen: metrics.circuitBreakerOpen,
      circuitBreakerFailures: metrics.circuitBreakerFailures
    };

    console.group('LogQueueManager Metrics Report');
    console.table(report);
    
    if (metrics.failedTransmissions > 0) {
      console.warn(`${metrics.failedTransmissions} failed transmissions detected`);
    }
    
    if (metrics.droppedLogs > 0) {
      console.warn(`${metrics.droppedLogs} logs dropped due to queue overflow or errors`);
    }
    
    if (metrics.circuitBreakerOpen) {
      console.error('Circuit breaker is OPEN - log transmission suspended');
    }
    
    console.groupEnd();
  }

  /**
   * Get environment-specific configuration
   */
  private getEnvironmentConfig(): LogQueueConfig {
    let envConfig: LoggingConfig;
    
    if (environment.production) {
      envConfig = PRODUCTION_LOGGING_CONFIG;
    } else if (environment.test) {
      envConfig = STAGING_LOGGING_CONFIG;
    } else {
      envConfig = DEVELOPMENT_LOGGING_CONFIG;
    }

    // Convert LoggingConfig to LogQueueConfig, using defaults for missing values
    return {
      batchSize: envConfig.batchSize ?? DEFAULT_CONFIG.batchSize,
      flushIntervalMs: envConfig.flushIntervalMs ?? DEFAULT_CONFIG.flushIntervalMs,
      maxRetries: envConfig.maxRetries ?? DEFAULT_CONFIG.maxRetries,
      maxQueueSize: envConfig.maxQueueSize ?? DEFAULT_CONFIG.maxQueueSize
    };
  }

  /**
   * Detect if running in development mode
   * @returns True if in development mode (not test or production)
   */
  private detectDevelopmentMode(): boolean {
    // Use Angular environment configuration
    // Only return true for actual development, not test or production
    return !environment.production && !environment.test;
  }

  /**
   * Test WriteLoggingService directly (for debugging)
   */
  async testWriteLoggingService(): Promise<void> {
    if (!this.writeLoggingService) {
      this.logDebug('WriteLoggingService not available');
      return;
    }

    const testLog: LogRecord = {
      timestamp: new Date().toISOString(),
      level: 'INFO',
      message: 'Test log from LogQueueManager',
      class: 'LogQueueManager',
      method: 'testWriteLoggingService',
      thread: 'main',
      kinective: {
        product: METADATA.PRODUCT_NAME,
        environment: 'development',
        service: METADATA.getServiceName('development'),
        correlationId: 'test-correlation-id',
        sessionId: 'test-session-id'
      },
      data: { test: true }
    };

    try {
      if (typeof this.writeLoggingService.writeStructuredLogBatch === 'function') {
        const logsWithSessionId = [{ record: testLog, sessionId: testLog.kinective.sessionId }];
        const result = await this.writeLoggingService.writeStructuredLogBatch(logsWithSessionId).toPromise();
        this.logDebug('WriteLoggingService test successful:', result);
      } else {
        this.logDebug('writeStructuredLogBatch method not available');
      }
    } catch (error) {
      this.logError('WriteLoggingService test failed:', error);
    }
  }

  /**
   * Get failed logs status for debugging
   */
  getFailedLogsStatus(): { count: number; retryAttempted: boolean; age: number } | null {
    const storedData = this.getStoredFailedLogs();
    if (!storedData) {
      return null;
    }

    return {
      count: storedData.logs.length,
      retryAttempted: storedData.retryAttempted,
      age: Date.now() - storedData.timestamp
    };
  }

  /**
   * Manually retry failed logs (for debugging)
   */
  async manualRetryFailedLogs(): Promise<void> {
    const storedData = this.getStoredFailedLogs();
    if (!storedData || storedData.logs.length === 0) {
      this.logDebug('No failed logs to retry');
      return;
    }

    this.logDebug(`Manually retrying ${storedData.logs.length} failed logs`);
    await this.retryFailedLogsWithAuth(storedData.logs);
  }
}
